import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { 
  CheckCircle2, 
  XCircle, 
  Clock,
  AlertTriangle,
  Pause,
  Play,
  Wrench,
  Settings,
  RefreshCw,
  LogOut,
  Zap,
  Target,
  TrendingUp
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a6fd91e7-1ad0-4cf0-91ce-b769f9bab27a/artifacts/m0y29wqk_Sem%20T%C3%ADtulo.png";

const STATUS_CONFIG = {
  running: { label: "EM PRODUÇÃO", color: "from-green-500 to-green-600", bg: "bg-green-500", text: "text-green-500", icon: Play },
  idle: { label: "PARADA", color: "from-amber-500 to-amber-600", bg: "bg-amber-500", text: "text-amber-500", icon: Pause },
  stopped: { label: "DESLIGADA", color: "from-red-500 to-red-600", bg: "bg-red-500", text: "text-red-500", icon: XCircle },
  maintenance: { label: "MANUTENÇÃO", color: "from-purple-500 to-purple-600", bg: "bg-purple-500", text: "text-purple-500", icon: Wrench },
  setup: { label: "SETUP", color: "from-blue-500 to-blue-600", bg: "bg-blue-500", text: "text-blue-500", icon: Settings },
};

function formatTime(seconds) {
  if (!seconds || seconds <= 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

function MachineCard({ data }) {
  const { machine, active_order, part, production, quality } = data;
  const statusConfig = STATUS_CONFIG[machine.status] || STATUS_CONFIG.idle;
  const StatusIcon = statusConfig.icon;
  
  const progress = active_order
    ? Math.min(100, (production.good_quantity / active_order.good_quantity_target) * 100)
    : 0;

  const hasAlert = quality?.rejection_rate > 5 || quality?.blocked;
  const qualityRate = production.good_quantity + production.rejected_quantity > 0
    ? ((production.good_quantity / (production.good_quantity + production.rejected_quantity)) * 100).toFixed(1)
    : 100;

  return (
    <div 
      className={`relative rounded-2xl overflow-hidden shadow-2xl transition-all duration-500 hover:scale-[1.02] ${
        hasAlert ? 'ring-4 ring-red-500 animate-pulse' : ''
      }`}
      style={{
        background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)"
      }}
    >
      {/* Status Bar */}
      <div className={`h-2 bg-gradient-to-r ${statusConfig.color}`} />
      
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${statusConfig.color} shadow-lg`}>
              <StatusIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-black text-gray-800 tracking-tight">{machine.code}</h2>
              <p className="text-gray-500 text-sm">{machine.description}</p>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-full bg-gradient-to-r ${statusConfig.color} shadow-lg`}>
            <span className="text-white font-bold text-lg">{statusConfig.label}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {active_order ? (
          <div className="space-y-5">
            {/* Alert Banner */}
            {hasAlert && (
              <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl p-4 flex items-center gap-3 shadow-lg animate-pulse">
                <AlertTriangle className="h-8 w-8 text-white" />
                <span className="text-white text-xl font-bold">
                  {quality?.blocked ? "⚠️ MÁQUINA BLOQUEADA" : `⚠️ REJEIÇÃO ALTA: ${quality?.rejection_rate}%`}
                </span>
              </div>
            )}

            {/* Order & Part Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <p className="text-blue-600 text-xs font-bold uppercase tracking-wider mb-1">Ordem</p>
                <p className="text-2xl font-black text-blue-800">{active_order.code}</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200">
                <p className="text-amber-600 text-xs font-bold uppercase tracking-wider mb-1">Peça</p>
                <p className="text-lg font-bold text-amber-800 truncate">{part?.description || "-"}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-gray-600" />
                  <span className="text-gray-600 font-medium">Progresso</span>
                </div>
                <span className="text-3xl font-black text-gray-800">{progress.toFixed(1)}%</span>
              </div>
              <div className="h-6 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                <div 
                  className={`h-full rounded-full bg-gradient-to-r ${
                    progress >= 90 ? 'from-green-400 to-green-500' : 
                    progress >= 50 ? 'from-blue-400 to-blue-500' : 
                    'from-amber-400 to-amber-500'
                  } transition-all duration-1000 shadow-lg`}
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              {/* Good */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-200">
                <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-black text-green-600">{production.good_quantity.toLocaleString()}</p>
                <p className="text-green-600 text-xs font-bold uppercase">Boas</p>
              </div>
              
              {/* Rejected */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 text-center border border-red-200">
                <XCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
                <p className="text-3xl font-black text-red-600">{production.rejected_quantity.toLocaleString()}</p>
                <p className="text-red-600 text-xs font-bold uppercase">Rejeitadas</p>
              </div>
              
              {/* Quality */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center border border-purple-200">
                <TrendingUp className="h-8 w-8 text-purple-500 mx-auto mb-2" />
                <p className="text-3xl font-black text-purple-600">{qualityRate}%</p>
                <p className="text-purple-600 text-xs font-bold uppercase">Qualidade</p>
              </div>
            </div>

            {/* Target */}
            <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center gap-3">
                <Zap className="h-6 w-6 text-amber-500" />
                <span className="text-gray-600 font-medium">Objetivo</span>
              </div>
              <span className="text-2xl font-black text-gray-800">
                {production.good_quantity.toLocaleString()} / {active_order.good_quantity_target.toLocaleString()}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
              <Pause className="h-10 w-10 text-gray-400" />
            </div>
            <p className="text-2xl font-bold text-gray-400">Sem ordem ativa</p>
            <p className="text-gray-400 mt-2">Aguardando próxima produção</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FactoryDisplay() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("mes_factory_token");
      if (!token) {
        navigate("/factory-login");
        return;
      }
      
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const response = await axios.get(`${API}/factory-display-data`);
      setMachines(response.data.machines || []);
      setLastUpdate(new Date());
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem("mes_factory_token");
        navigate("/factory-login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
    const dataInterval = setInterval(fetchData, 10000); // Update every 10 seconds
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(dataInterval);
      clearInterval(clockInterval);
    };
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("mes_factory_token");
    navigate("/factory-login");
  };

  // Stats
  const runningCount = machines.filter(m => m.machine.status === "running").length;
  const stoppedCount = machines.filter(m => m.machine.status === "stopped" || m.machine.status === "idle").length;
  const totalGood = machines.reduce((sum, m) => sum + (m.production?.good_quantity || 0), 0);
  const totalRejected = machines.reduce((sum, m) => sum + (m.production?.rejected_quantity || 0), 0);

  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 100%)" }}
      >
        <div className="text-center">
          <div className="bg-white rounded-xl p-4 inline-block mb-8">
            <img src={LOGO_URL} alt="EP3D" className="h-24 object-contain" />
          </div>
          <RefreshCw className="h-16 w-16 text-amber-400 mx-auto animate-spin" />
          <p className="text-white text-2xl mt-6 font-medium">A carregar dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 50%, #1b263b 100%)" }}
      data-testid="factory-display-page"
    >
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/30 border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Title */}
            <div className="flex items-center gap-6">
              <div className="bg-white rounded-lg p-2">
                <img 
                  src={LOGO_URL} 
                  alt="EP3D Engineering Project" 
                  className="h-12 object-contain"
                />
              </div>
              <div className="hidden md:block border-l border-white/20 pl-6">
                <h1 className="text-2xl font-bold text-white">PAINEL DE PRODUÇÃO</h1>
                <p className="text-amber-400 text-sm font-medium">Monitorização em Tempo Real</p>
              </div>
            </div>

            {/* Stats Pills */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="flex items-center gap-2 bg-green-500/20 backdrop-blur px-4 py-2 rounded-full border border-green-500/30">
                <Play className="h-5 w-5 text-green-400" />
                <span className="text-green-400 font-bold">{runningCount} Em Produção</span>
              </div>
              <div className="flex items-center gap-2 bg-amber-500/20 backdrop-blur px-4 py-2 rounded-full border border-amber-500/30">
                <Pause className="h-5 w-5 text-amber-400" />
                <span className="text-amber-400 font-bold">{stoppedCount} Paradas</span>
              </div>
            </div>

            {/* Time & Logout */}
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-4xl font-black text-white tracking-tight">
                  {currentTime.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })}
                </p>
                <p className="text-amber-400 text-sm font-medium">
                  {currentTime.toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })}
                </p>
              </div>
              <button
                onClick={handleLogout}
                className="p-3 rounded-xl bg-white/10 hover:bg-white/20 transition-colors border border-white/20"
                title="Sair"
              >
                <LogOut className="h-6 w-6 text-white" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Summary Bar */}
      <div className="bg-black/20 backdrop-blur border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-4xl font-black text-white">{machines.length}</p>
              <p className="text-gray-400 text-sm font-medium uppercase">Máquinas</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-green-400">{runningCount}</p>
              <p className="text-gray-400 text-sm font-medium uppercase">Em Produção</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-amber-400">{totalGood.toLocaleString()}</p>
              <p className="text-gray-400 text-sm font-medium uppercase">Peças Boas</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-black text-red-400">{totalRejected.toLocaleString()}</p>
              <p className="text-gray-400 text-sm font-medium uppercase">Rejeitadas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Machines Grid */}
      <main className="container mx-auto px-6 py-8">
        {machines.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-white/10 mb-6">
              <Settings className="h-12 w-12 text-white/50" />
            </div>
            <p className="text-2xl text-white/70">Nenhuma máquina configurada</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {machines.map((data) => (
              <MachineCard key={data.machine.id} data={data} />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-black/30 backdrop-blur border-t border-white/10">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 text-green-400 animate-spin" />
              <span className="text-white/50 text-sm">
                Última atualização: {lastUpdate.toLocaleTimeString("pt-PT")}
              </span>
            </div>
            <span className="text-white/30 text-sm">
              EP3D ENGINEERING PROJECT © {new Date().getFullYear()}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
